import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { CLIENT_STATUS_STYLES } from "../statusStyles";
import { avatarTintFor } from "../avatarTints";
import type { UserClient } from "../types/users.types";

// There was a "Clients" column between Location and Joined on. It printed a
// count of a client's own clients, which is not a thing a dietitian's client
// has, and the endpoint sends nothing of the kind — the width it held is
// spread across the four that remain.
const COLUMNS = [
  { heading: "User name", width: "w-[34%]" },
  { heading: "Location", width: "w-[26%]" },
  { heading: "Joined on", width: "w-[20%]" },
  { heading: "Status", width: "w-[20%]" },
];

interface UserClientsTableProps {
  clients: UserClient[];
  /** Stands in for the body when the roster comes back with no rows. */
  empty: EmptyStateProps;
  /**
   * Where the rows are. While they are on their way the body says nothing — no
   * rows and no message — and only a load slow enough to notice shimmers, as
   * one block the size of the table. Rows already in hand are left alone
   * either way, so returning to a tab shows the page it was left on rather
   * than shimmering over it.
   */
  phase?: LoadPhase;
}

/**
 * The clients on one account's own roster.
 *
 * Read-only, and deliberately: this console is here to answer questions about
 * a Naurist customer, not to reach into the practice they run. Nothing in a
 * row is clickable, because there is no screen behind it and there should not
 * be one — a client of a customer is that customer's record, not ours to open.
 */
const UserClientsTable: React.FC<UserClientsTableProps> = ({
  clients,
  empty,
  phase = "ready",
}) => {
  if (phase !== "ready" && clients.length === 0) {
    // The table's own area, standing in for the whole list — see
    // `TABLE_BODY_SKELETON`. Hidden from screen readers with the rest of the
    // placeholders; the card above says it is loading in words.
    return (
      <div className="p-4 sm:p-6">
        <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto custom-scrollbar-overlay">
      <table className="w-full table-fixed border-collapse text-left min-w-[700px]">
        <colgroup>
          {COLUMNS.map((column) => (
            <col key={column.heading} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.heading}
                scope="col"
                className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {column.heading}
              </th>
            ))}
          </tr>
        </thead>

        {clients.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {clients.map((client) => (
              <tr key={client.id} className="border-b border-line last:border-0">
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-8 h-8 3xl:w-9 3xl:h-9 rounded-full flex items-center justify-center flex-shrink-0 ${avatarTintFor(
                        client.name
                      )}`}
                    >
                      <span className="text-[13px] font-medium">
                        {client.name.charAt(0)}
                      </span>
                    </span>
                    <span className="text-[14px] text-ink truncate">
                      {client.name}
                    </span>
                  </div>
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink truncate">
                  {client.location}
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink whitespace-nowrap">
                  {client.joinedOn}
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      CLIENT_STATUS_STYLES[client.status]
                    }`}
                  >
                    {client.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          )}
      </table>
    </div>
  );
};

export default UserClientsTable;
